import { eventBus, subscribe } from 'mobx-event-bus2';
import { AxiosError, AxiosProgressEvent, AxiosResponse, CancelTokenSource } from 'axios';

import { action, runInAction, makeObservable, observable } from 'mobx';

import DetailsActivities from 'ui/stores/DetailsActivities';
import { ActivityListResponse, FileActivityResponse } from 'ui/types';
import { ActivityType } from 'utils/stores/Activities/constants';

import { PaginationParams } from 'utils/api/types';
import { UserSearchResponse } from 'utils/api/modules/user/user.types';

import Logger from 'utils/logger';

import API from 'utils/api';
import RootStore from 'stores/Root';
import { EventType } from 'utils/events/constants';
import { ActionEvent } from 'utils/events/types';
import { EntityModules, PAGE_SIZE } from 'core/constants';

/** Which anchor an audit's files hang off: a submitted (or migrated) audit uses its submission, an
 * in-progress one its draft. Record-level and per-question files share the host. */
export type AuditHostKind = 'submission' | 'draft';

type AuditAttachmentHost = { host: 'submission' } | { host: 'draft'; draftUuid: string };

/** Callers pass the kind and uuid separately so a caller's effect can depend on plain values. */
const auditHostFrom = (kind: AuditHostKind | null, draftUuid: string | null): AuditAttachmentHost | null => {
  if (kind === 'submission') {
    return { host: 'submission' };
  }

  return kind === 'draft' && draftUuid ? { host: 'draft', draftUuid } : null;
};

export default class EntitiesActivities extends DetailsActivities<RootStore, typeof API> {
  entityId: string;

  entityType: EntityModules;

  /** Set by `loadAuditAttachments`; upload and delete route through it. */
  private auditHost: AuditAttachmentHost | null = null;

  @observable attachments: Array<FileActivityResponse & { isSubmission: boolean }> = [];

  @observable loadingAttachments = false;

  /** False until a load actually succeeds, so a failed fetch is not mistaken for "no files". */
  @observable attachmentsLoaded = false;

  @observable loadingMoreAttachments = false;

  @observable attachmentsHasMore = false;

  @observable deletingFile: string | null = null;

  constructor(rootStore: RootStore, api: typeof API, entityId: string, entityType: EntityModules) {
    super(rootStore, api);

    makeObservable(this);

    this.entityId = entityId;
    this.entityType = entityType;

    // Audits never load the record-level feed (it 500s for them), but the shared upload machinery
    // tracks progress rows on `activities` and bails outright while that list is undefined.
    if (entityType === EntityModules.Audits) {
      this.activities = [];
    }

    eventBus.register(this);
  }

  async loadActivitiesData(params: PaginationParams): Promise<AxiosResponse<ActivityListResponse>> {
    return this.api.entities.loadEntityActivities(this.entityType, this.entityId, params)();
  }

  @action.bound
  async addMessage(message: string, taggedUsers?: UserSearchResponse[]): Promise<void> {
    if (this.error || !this.activities) {
      return;
    }
    try {
      const { data } = await this.api.entities.addEntityMessage(this.entityType, this.entityId, message, taggedUsers)();
      runInAction(() => {
        if (this.activities) {
          this.activities = [...this.activities, { ...data, type: ActivityType.Message }];
        }
      });
    } catch (e) {
      Logger.error(`Invalid add message API result: ${this.entityType} ID: ${this.entityId}. Message: ${message}`, e);
    }
  }

  private handleUploadError(e: unknown): void {
    const error = e as AxiosError<{ file: string[] }>;
    // response is of type { file: string }
    const fieldErrors = error.response?.data?.file;
    if (fieldErrors) {
      // Expected validation failure (e.g. file too large) — surface to the user, don't report to Sentry.
      fieldErrors.forEach((message) => this.rootStore.notification.enqueueErrorSnackbar(message));
    } else {
      // Unexpected failure — capture the AxiosError so status/response are available for diagnosis.
      Logger.error(`Invalid add file API result: ${this.entityType} ID: ${this.entityId}.`, error);
      this.rootStore.notification.enqueueErrorSnackbar('Failed to upload file');
    }
  }

  async doUploadFile(
    file: File,
    onUpdateProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): Promise<void> {
    if (this.entityType === EntityModules.Audits) {
      await this.doUploadAuditFile(file, onUpdateProgress, cancelTokenSource);
      return;
    }

    try {
      const { data } = await this.api.entities.uploadEntityFile(
        this.entityType,
        this.entityId,
        file,
        onUpdateProgress,
        cancelTokenSource
      )();
      runInAction(() => {
        if (this.activities) {
          this.activities = [...this.activities, data];
          this.attachments = [
            ...this.attachments,
            { ...data, isSubmission: false } as FileActivityResponse & { isSubmission: boolean },
          ];
        }
      });
    } catch (e) {
      this.handleUploadError(e);
    }
  }

  /** Posted without a `questionId`, which the host endpoints record as a record-level file. */
  async doUploadAuditFile(
    file: File,
    onUpdateProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): Promise<void> {
    const host = this.auditHost;
    if (!host) {
      return;
    }

    try {
      const { data } = await (host.host === 'draft'
        ? this.api.entities.uploadDraftFile(
            this.entityType,
            host.draftUuid,
            file,
            onUpdateProgress,
            cancelTokenSource
          )()
        : this.api.entities.uploadEntitySubmissionFile(
            this.entityType,
            this.entityId,
            file,
            onUpdateProgress,
            cancelTokenSource
          )());

      runInAction(() => {
        this.attachments = [
          ...this.attachments,
          { ...data, isSubmission: true } as FileActivityResponse & { isSubmission: boolean },
        ];
      });
    } catch (e) {
      this.handleUploadError(e);
    }
  }

  /** Audit files hang off the submission/draft host, not the entity, so they delete through the
   * host route; the record-level entity route stays closed for audits. */
  @action.bound
  async deleteAuditFile(fileId: string): Promise<void> {
    const host = this.auditHost;
    if (!host) {
      return;
    }

    this.deletingFile = fileId;

    try {
      await (host.host === 'draft'
        ? this.api.entities.removeDraftFile(this.entityType, host.draftUuid, fileId)()
        : this.api.entities.removeEntitySubmissionFile(this.entityType, this.entityId, fileId)());

      runInAction(() => {
        this.removeActivity(fileId);
        this.attachments = this.attachments.filter((attachment) => attachment.uuid !== fileId);
      });
    } catch (e) {
      Logger.error(`Invalid delete audit file API result. Audit ID: ${this.entityId}. File ID: ${fileId}`, e);
      this.rootStore.notification.enqueueErrorSnackbar('Failed to delete attachment');
    } finally {
      runInAction(() => {
        this.deletingFile = null;
      });
    }
  }

  @action.bound
  async deleteFile(fileId: string): Promise<void> {
    if (this.entityType === EntityModules.Audits) {
      await this.deleteAuditFile(fileId);
      return;
    }

    this.deletingFile = fileId;

    if (this.activities === undefined) {
      return;
    }

    const file = this.activities.find((activity) => activity.uuid === fileId);
    if (file) {
      try {
        await this.api.entities.removeEntityFile(this.entityType, this.entityId, fileId)();
        runInAction(() => {
          this.removeActivity(file.uuid);
          this.attachments = this.attachments.filter((attachment) => attachment.uuid !== fileId);
        });
      } catch (e) {
        Logger.error(`Invalid delete file API result: ${this.entityType} ID: ${this.entityId}. File ID: ${fileId}`, e);
      } finally {
        this.deletingFile = null;
      }
    }
  }

  @action.bound
  async deleteMessage(messageId: string): Promise<void> {
    if (this.activities === undefined) {
      return;
    }
    const message = this.activities.find((activity) => activity.uuid === messageId);
    if (message) {
      try {
        await this.api.entities.removeEntityMessage(this.entityType, this.entityId, messageId)();
        runInAction(() => {
          if (this.activities !== undefined) {
            this.activities = this.activities.filter((activity) => activity.uuid !== messageId);
          }
        });
      } catch (e) {
        Logger.error(
          `Invalid delete message API result: ${this.entityType} ID: ${this.entityId}. Message ID: ${messageId}`,
          e
        );
      }
    }
  }

  @subscribe(EventType.UpdatedSite)
  @subscribe(EventType.UpdatedEvent)
  @action
  updatedTask({ payload }: ActionEvent<{ uuid: string }>): void {
    if (this.activities !== undefined && this.entityId === payload.uuid) {
      this.activities = undefined;
      this.loadActivities();
    }
  }

  /** An audit's files hang off its host, not the entity: the record-level entity endpoint 500s for
   * AUDITS and the catch below would swallow that into an empty list. Unpaginated, so no `hasMore`. */
  @action.bound
  async loadAuditAttachments(hostKind: AuditHostKind | null, draftUuid: string | null): Promise<void> {
    const host = auditHostFrom(hostKind, draftUuid);
    this.auditHost = host;
    this.loadingAttachments = true;
    this.attachmentsLoaded = false;

    // A scheduled audit that was never opened has neither host yet: skip rather than show a
    // swallowed error.
    if (!host) {
      runInAction(() => {
        this.attachments = [];
        this.attachmentsHasMore = false;
        this.loadingAttachments = false;
        this.attachmentsLoaded = true;
      });
      return;
    }

    try {
      const { data } = await (host.host === 'draft'
        ? this.api.entities.loadDraftFiles(this.entityType, host.draftUuid)()
        : this.api.entities.loadEntitySubmissions(this.entityType, this.entityId)());

      runInAction(() => {
        this.attachments = data.map((file) => ({ ...file, isSubmission: true }));
        this.attachmentsHasMore = false;
        this.attachmentsLoaded = true;
      });
    } catch (e) {
      Logger.error(`Invalid load audit attachments API result. Audit ID: ${this.entityId}.`, e);
    } finally {
      runInAction(() => {
        this.loadingAttachments = false;
      });
    }
  }

  // load attachments
  @action.bound
  async loadAttachments(
    loadMore = false,
    auditHostKind: AuditHostKind | null = null,
    auditDraftUuid: string | null = null
  ): Promise<void> {
    if (this.entityType === EntityModules.Audits) {
      if (!loadMore) {
        await this.loadAuditAttachments(auditHostKind, auditDraftUuid);
      }
      return;
    }

    if (loadMore) {
      this.loadingMoreAttachments = true;
    } else {
      this.loadingAttachments = true;
    }

    // Activity-side attachments are paginated; submission attachments come from a separate
    // non-paginated endpoint and only need to be fetched on the initial load.
    const offset = loadMore ? this.attachments.filter((a) => !a.isSubmission).length : 0;

    try {
      const {
        data: { results, next },
      } = await this.api.entities.loadEntityAttachments(this.entityType, this.entityId, {
        limit: PAGE_SIZE,
        offset,
      })();

      let submissions: FileActivityResponse[] = [];
      if (!loadMore && this.entityType === EntityModules.Events) {
        const { data: submissionsData } = await this.api.entities.loadEntitySubmissions(
          this.entityType,
          this.entityId
        )();
        submissions = submissionsData;
      }

      runInAction(() => {
        const activityAttachments = results.map((file) => ({ ...file, isSubmission: false }));
        if (loadMore) {
          this.attachments = [...this.attachments, ...activityAttachments];
        } else {
          this.attachments = [
            ...activityAttachments,
            ...(this.entityType === EntityModules.Events
              ? submissions.map((file) => ({ ...file, isSubmission: true }))
              : []),
          ];
        }
        this.attachmentsHasMore = next !== null;
        this.attachmentsLoaded = true;
      });
    } catch (e) {
      Logger.error(`Invalid load attachments API result: ${this.entityType} ID: ${this.entityId}.`, e);
    } finally {
      runInAction(() => {
        this.loadingAttachments = false;
        this.loadingMoreAttachments = false;
      });
    }
  }
}
